/**
 * Style2 (Classic 50/50) style-specific section renderers.
 * Extracted from Classic5050Style2.jsx for use in GridEngine.
 * Gradient all-in-one hero with partner logo, countdown, massive jackpot.
 */
import React from 'react'
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Share2 from 'lucide-react/dist/esm/icons/share-2.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Heart from 'lucide-react/dist/esm/icons/heart.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import InlineTranslationEdit from '../../components/InlineTranslationEdit'
import HeroPartnerGlassSelect from '../../components/HeroPartnerGlassSelect'
import { formatCurrencyValue } from '../../utils/formatCurrency'

// Hero: Charity-gradient card with partner logo, countdown, massive jackpot, CTA
export function Style2HeroSection({
  mainDraw, partner, t, editMode, handleTranslationUpdate,
  purchaseNavigate, timeLeft, loading, language,
}) {
  const c = t
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700 h-96 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">{c?.('classic5050.loadingDraws', 'Loading draws...')}</div>
      </div>
    )
  }
  if (!mainDraw) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700">
        <p className="text-gray-600 dark:text-gray-400 text-center">
          {c?.('classic5050.noActiveDraws', 'No active draws at the moment.')}
        </p>
      </div>
    )
  }

  const drawDateLabel = mainDraw.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : ''

  return (
    <div
      className="rounded-3xl shadow-2xl overflow-hidden p-8 md:p-12 relative"
      style={{
        backgroundImage: 'linear-gradient(135deg, var(--charity-gradient-start), var(--charity-gradient-end))',
        color: 'var(--charity-primary-contrast)',
      }}
    >
      <div className="absolute inset-0 bg-black/10 backdrop-blur-sm pointer-events-none" aria-hidden="true" />
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full -mr-48 -mt-48 animate-float-slow blur-3xl opacity-20 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }} aria-hidden="true" />
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full -ml-32 -mb-32 animate-float-medium blur-3xl opacity-10 pointer-events-none"
        style={{ backgroundColor: 'var(--charity-primary-contrast)' }} aria-hidden="true" />

      <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
        {partner?.partner_logo_url && (
          <div className="flex-shrink-0">
            <div className="bg-white rounded-3xl p-6 shadow-2xl w-40 h-40 md:w-48 md:h-48 flex items-center justify-center">
              <img
                src={partner.partner_logo_url}
                alt={partner.partner_name || c?.('common.charityPartner', 'Charity Partner')}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          </div>
        )}

        <div className="flex-1 text-center">
          <div className="mb-6">
            <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 mb-6">
              <Trophy className="w-6 h-6 text-yellow-300 animate-pulse" />
              <span className="text-white font-bold text-lg">
                {mainDraw.title || c?.('gameTypes.classic5050', 'Classic 50/50 Draw')}
              </span>
            </div>
          </div>

          {mainDraw.draw_date && (
            <div className="mb-6">
              <div className="text-white/80 text-sm font-medium mb-3">
                <InlineTranslationEdit translationKey="HomeStyle2DrawCountdown" value={c?.('HomeStyle2DrawCountdown', 'Draw Countdown')} fallback="Draw Countdown" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </div>
              <div className="flex items-center justify-center gap-3">
                {[
                  { value: timeLeft?.days || 0, label: c?.('countdown.days', 'Days') },
                  { value: timeLeft?.hours || 0, label: c?.('countdown.hours', 'Hours') },
                  { value: timeLeft?.minutes || 0, label: c?.('countdown.minutes', 'Mins') },
                  { value: timeLeft?.seconds || 0, label: c?.('countdown.seconds', 'Secs') },
                ].map((unit, i) => (
                  <React.Fragment key={unit.label}>
                    {i > 0 && <div className="text-white text-2xl font-bold">:</div>}
                    <div className="bg-white/20 backdrop-blur-md rounded-lg px-4 py-3 min-w-[70px]">
                      <div className="text-white text-2xl md:text-3xl font-black">{String(unit.value).padStart(2, '0')}</div>
                      <div className="text-white/80 text-xs uppercase tracking-wider">{unit.label}</div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <h1 className="text-white text-6xl md:text-8xl font-black mb-4 drop-shadow-2xl">
            {formatCurrencyValue(mainDraw.current_jackpot, { language })}
          </h1>

          <p className="text-white/90 text-2xl font-semibold mb-6 drop-shadow-lg">
            <InlineTranslationEdit translationKey="HomeStyle2JackpotSubtitle" value={c?.('HomeStyle2JackpotSubtitle', 'Current Jackpot - 50% Goes to Winner!')} fallback="Current Jackpot - 50% Goes to Winner!" onUpdate={handleTranslationUpdate} editMode={editMode} />
          </p>

          <HeroPartnerGlassSelect className="mx-auto mb-6 w-full max-w-md" />

          <div className="flex items-center justify-center gap-4 mb-8">
            <button
              onClick={() => purchaseNavigate?.('/purchase')}
              className="group relative px-8 py-4 font-bold text-lg rounded-xl shadow-2xl transition-all hover:scale-105 active:scale-95"
              style={{ backgroundColor: 'var(--charity-primary)', color: 'var(--charity-primary-contrast)' }}
            >
              <span className="flex items-center gap-2">
                <Ticket className="w-6 h-6" />
                <InlineTranslationEdit translationKey="HomeStyle2CTAButton" value={c?.('HomeStyle2CTAButton', 'Buy Tickets Now')} fallback="Buy Tickets Now" onUpdate={handleTranslationUpdate} editMode={editMode} />
              </span>
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 min-w-[180px] text-center">
              <Clock className="w-8 h-8 text-white mx-auto mb-2" />
              <div className="text-white text-sm font-medium">{c?.('classic5050.drawDate', 'Draw Date')}</div>
              <div className="text-white font-bold">{drawDateLabel || c?.('progressive.tba', 'TBA')}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 min-w-[180px] text-center">
              <DollarSign className="w-8 h-8 text-white mx-auto mb-2" />
              <div className="text-white text-sm font-medium">{c?.('purchase.ticketPrice', 'Ticket Price')}</div>
              <div className="text-white font-bold">${mainDraw.ticket_price ?? 5}</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20 min-w-[180px] text-center">
              <Heart className="w-8 h-8 text-white mx-auto mb-2" />
              <div className="text-white text-sm font-medium">{c?.('classic5050.splitJackpot', '50/50 Split')}</div>
              <div className="text-white font-bold">{c?.('classic5050.winnerCharity', 'Winner + Charity')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
